import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfYear,
  format,
  startOfDay,
  startOfYear
} from "date-fns";
import { db, ensureAnon } from "./firebase";
import { useAuth } from "./auth/AuthContext";

const allowedEmails = ["dunderjeep@gmail.com"];
const allowedUids = ["VvBUVY6rN5Xg358oNXW2eoUzQX92"];

function StatPill({ label, value, accent }) {
  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.25)",
        background: "rgba(255,255,255,0.12)",
        color: "#f8fafc",
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 12, letterSpacing: 0.3, opacity: 0.9 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, color: accent || "#fef08a" }}>{value}</div>
    </div>
  );
}

function DayCell({ date, selected, filled, onSelect }) {
  const label = format(date, "MMM d");
  const key = format(date, "yyyy-MM-dd");
  const shade = selected ? "#0f172a" : filled ? "#0ea5e9" : "rgba(255,255,255,0.16)";
  return (
    <button
      onClick={() => onSelect(date)}
      title={`${label}${filled ? " • saved" : ""}`}
      style={{
        height: 28,
        width: "100%",
        borderRadius: 8,
        border: selected ? "2px solid #fef3c7" : "1px solid rgba(255,255,255,0.25)",
        background: shade,
        color: selected ? "#fef3c7" : "#e2e8f0",
        fontSize: 10,
        fontWeight: 700,
        cursor: "pointer",
        boxShadow: selected ? "0 0 0 2px rgba(254,243,199,0.35)" : "none",
        transition: "transform 120ms ease, box-shadow 120ms ease",
      }}
      aria-pressed={selected}
      aria-label={`Select ${key}`}
    >
      {format(date, "d")}
    </button>
  );
}

export default function Cortisolace() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [entries, setEntries] = useState({});
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [anonReady, setAnonReady] = useState(false);

  const providerEmails = (user?.providerData || [])
    .map((p) => p.email)
    .filter(Boolean)
    .map((e) => e.toLowerCase());
  const effectiveEmail = (user?.email || providerEmails[0] || "").toLowerCase();
  const isOwner = allowedEmails.includes(effectiveEmail) || (user && allowedUids.includes(user.uid));

  const selectedKey = format(selectedDate, "yyyy-MM-dd");
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const selectedEntry = entries[selectedKey];
  const todayEntry = entries[todayKey];

  const col = useMemo(() => collection(db, "cortisolace_days"), []);

  // Allow anonymous viewers to read (Firestore rules require an auth session).
  useEffect(() => {
    if (user) {
      setAnonReady(true);
      return;
    }
    let active = true;
    ensureAnon()
      .then(() => {
        if (active) setAnonReady(true);
      })
      .catch((err) => {
        console.error("[Cortisolace] anon auth", err);
        if (active) setStatus("Could not start anonymous session; try refreshing.");
      });
    return () => {
      active = false;
    };
  }, [user]);

  // Keep form inputs in sync with the selected date.
  const [ifText, setIfText] = useState("");
  const [thenText, setThenText] = useState("");
  useEffect(() => {
    setIfText(selectedEntry?.ifText || "");
    setThenText(selectedEntry?.thenText || "");
  }, [selectedEntry?.ifText, selectedEntry?.thenText, selectedKey]);

  // Subscribe to the year's worth of entries.
  useEffect(() => {
    if (!user && !anonReady) return undefined;
    const qy = query(col, orderBy("dateKey"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          const key = data.dateKey || d.id;
          map[key] = { id: d.id, ...data };
        });
        setEntries(map);
      },
      (err) => {
        console.error("[Cortisolace] load", err);
        setStatus(err.message || "Failed to load calendar.");
      }
    );
    return () => unsub();
  }, [user, col, anonReady]);

  const yearDays = useMemo(() => {
    const start = startOfYear(selectedDate);
    const end = endOfYear(start);
    return eachDayOfInterval({ start, end });
  }, [selectedDate]);

  const filledCount = useMemo(() => {
    return yearDays.filter((d) => {
      const key = format(d, "yyyy-MM-dd");
      return !!entries[key]?.ifText || !!entries[key]?.thenText;
    }).length;
  }, [entries, yearDays]);

  const progressPct = yearDays.length ? Math.round((filledCount / yearDays.length) * 100) : 0;

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!isOwner) {
      setStatus("Only the owner account can update Cortisolace.");
      return;
    }
    setSaving(true);
    setStatus("Saving…");
    try {
      const dayNumber = differenceInCalendarDays(selectedDate, startOfYear(selectedDate)) + 1;
      await setDoc(
        doc(db, "cortisolace_days", selectedKey),
        {
          dateKey: selectedKey,
          year: selectedDate.getFullYear(),
          dayOfYear: dayNumber,
          ifText: ifText.trim(),
          thenText: thenText.trim(),
          ownerUid: user?.uid || "",
          ownerEmail: effectiveEmail,
          updatedAt: serverTimestamp(),
          createdAt: selectedEntry?.createdAt || serverTimestamp(),
        },
        { merge: true }
      );
      setStatus("Saved ✓");
    } catch (err) {
      console.error("[Cortisolace] save", err);
      setStatus(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const jumpDays = (delta) => {
    setSelectedDate((prev) => startOfDay(addDays(prev, delta)));
  };

  const managementGuide = [
    "Pick a date from the strip or jump with the arrows—365/366 slots per year.",
    "Fill in your daily If/Then and hit Save to upsert into Firestore under cortisolace_days/{yyyy-MM-dd}.",
    "If you edit later, the day keeps its createdAt and only updates updatedAt.",
    "Progress pills show how many days in the current year have entries.",
    "Keep an eye on Today so you always see the live trigger while you tweak future days.",
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 15% 20%, #0ea5e9 0, #0b142c 35%, #0a0f1f 75%)",
        color: "#e2e8f0",
        padding: "32px 18px 72px",
        fontFamily: "Inter, 'Segoe UI', system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <header style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
          <div>
            <div style={{ display: "inline-block", padding: "6px 12px", borderRadius: 999, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", fontWeight: 700, letterSpacing: 0.4 }}>
              Cortisolace · If / Then / When
            </div>
            <h1 style={{ margin: "10px 0 4px", fontSize: 36, color: "#f8fafc" }}>365-day trigger calendar</h1>
            <p style={{ margin: 0, maxWidth: 760, color: "rgba(226,232,240,0.82)" }}>
              A private, owner-managed calendar for daily If/Then prompts. Manage every day of the year in one place and keep Today in view.
            </p>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <StatPill label="Year coverage" value={`${filledCount}/${yearDays.length} days`} />
            <StatPill label="Completion" value={`${progressPct || 0}%`} accent="#fef08a" />
            <StatPill label="Today" value={todayEntry?.ifText ? "Ready" : "Unset"} accent="#a5f3fc" />
          </div>
        </header>

        <div style={{ marginTop: 26, display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 18 }}>
          <section style={{ padding: 20, borderRadius: 18, background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 10px 50px rgba(0,0,0,0.35)" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, letterSpacing: 0.5, textTransform: "uppercase", color: "rgba(226,232,240,0.7)" }}>Selected</span>
              <strong style={{ fontSize: 20 }}>{format(selectedDate, "EEEE, MMM d yyyy")}</strong>
              <span style={{ padding: "6px 10px", borderRadius: 10, background: "rgba(255,255,255,0.08)", fontWeight: 700 }}>
                Day {differenceInCalendarDays(selectedDate, startOfYear(selectedDate)) + 1}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button onClick={() => jumpDays(-1)} style={navButtonStyle}>◀ Yesterday</button>
                <button onClick={() => setSelectedDate(startOfDay(new Date()))} style={navButtonStyle}>Today</button>
                <button onClick={() => jumpDays(1)} style={navButtonStyle}>Tomorrow ▶</button>
              </div>
            </div>

            <div style={{ marginTop: 16, display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
              <div style={ifThenCardStyle}>
                <div style={pillLabel}>If</div>
                <div style={ifThenText}>{selectedEntry?.ifText || "Add your condition or trigger."}</div>
              </div>
              <div style={ifThenCardStyle}>
                <div style={{ ...pillLabel, background: "rgba(239,68,68,0.24)", color: "#fecdd3", border: "1px solid rgba(239,68,68,0.35)" }}>Then</div>
                <div style={ifThenText}>{selectedEntry?.thenText || "Add the response or ritual you want."}</div>
              </div>
            </div>

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(14, minmax(0, 1fr))", gap: 6 }}>
              {yearDays.map((day) => (
                <DayCell
                  key={day.toISOString()}
                  date={day}
                  selected={format(day, "yyyy-MM-dd") === selectedKey}
                  filled={!!entries[format(day, "yyyy-MM-dd")]}
                  onSelect={setSelectedDate}
                />
              ))}
            </div>
          </section>

          <section style={{ padding: 20, borderRadius: 18, background: "#0b1326", border: "1px solid rgba(255,255,255,0.06)", boxShadow: "0 12px 40px rgba(0,0,0,0.4)", display: "grid", gap: 14 }}>
            {isOwner ? (
              <form onSubmit={handleSave} style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={labelStyle}>If</label>
                  <textarea
                    value={ifText}
                    onChange={(e) => setIfText(e.target.value)}
                    rows={3}
                    placeholder="If I wake up tense, if I see the sun at 7:30am..."
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Then</label>
                  <textarea
                    value={thenText}
                    onChange={(e) => setThenText(e.target.value)}
                    rows={3}
                    placeholder="Then I will stretch + make tea, Then I journal for 5 minutes..."
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="date"
                    value={selectedKey}
                    onChange={(e) => {
                      const next = new Date(e.target.value);
                      if (!Number.isNaN(next.getTime())) {
                        setSelectedDate(startOfDay(next));
                      }
                    }}
                    style={{ ...inputStyle, width: 180, padding: "10px 12px" }}
                  />
                  <button type="submit" disabled={saving} style={saveButtonStyle}>
                    {saving ? "Saving…" : "Save day to Firestore"}
                  </button>
                  {status && <span style={{ color: "#a5f3fc", fontWeight: 600 }}>{status}</span>}
                </div>
              </form>
            ) : (
              <div style={{ padding: 16, borderRadius: 12, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}>
                <strong>Read-only</strong>
                <p style={{ marginTop: 6, marginBottom: 0 }}>Sign into the owner account to edit Cortisolace. Current user cannot update these entries.</p>
              </div>
            )}

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>How to run Cortisolace</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: "rgba(226,232,240,0.85)", display: "grid", gap: 6 }}>
                {managementGuide.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p style={{ marginTop: 10, color: "rgba(148,163,184,0.9)", fontSize: 13 }}>
                Stored under <code style={{ background: "rgba(15,23,42,0.7)", padding: "2px 6px", borderRadius: 6 }}>cortisolace_days</code> with <code>dateKey</code> ids.
              </p>
            </div>
          </section>
        </div>

        <section style={{ marginTop: 28, padding: 18, borderRadius: 16, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 14, letterSpacing: 0.4, textTransform: "uppercase", color: "rgba(226,232,240,0.7)" }}>Today&apos;s trigger</div>
              <h3 style={{ margin: "6px 0 8px" }}>{format(new Date(), "EEEE, MMM d")}</h3>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ ...ifThenCardStyle, background: "rgba(15,23,42,0.45)", borderColor: "rgba(255,255,255,0.1)" }}>
                  <div style={pillLabel}>If</div>
                  <div style={ifThenText}>{todayEntry?.ifText || "Not set yet."}</div>
                </div>
                <div style={{ ...ifThenCardStyle, background: "rgba(15,23,42,0.45)", borderColor: "rgba(255,255,255,0.1)" }}>
                  <div style={{ ...pillLabel, background: "rgba(239,68,68,0.24)", color: "#fecdd3", border: "1px solid rgba(239,68,68,0.35)" }}>Then</div>
                  <div style={ifThenText}>{todayEntry?.thenText || "Not set yet."}</div>
                </div>
              </div>
            </div>
            <div style={{ minWidth: 260, maxWidth: 460 }}>
              <div style={{ fontSize: 14, letterSpacing: 0.4, textTransform: "uppercase", color: "rgba(226,232,240,0.7)" }}>Intent</div>
              <p style={{ margin: "6px 0 10px", color: "rgba(226,232,240,0.86)" }}>
                Stay consistent by always having a visible If/Then for the day. Update in advance, or iterate daily; Firestore keeps the latest.
              </p>
              <div style={{ display: "grid", gap: 6, color: "#cbd5e1", fontSize: 14 }}>
                <div>• Owner-only write access in UI.</div>
                <div>• Uses ISO date keys (<code>{todayKey}</code>) for clean lookups.</div>
                <div>• Day numbers auto-calc from the selected year.</div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const navButtonStyle = {
  border: "1px solid rgba(255,255,255,0.3)",
  background: "rgba(255,255,255,0.12)",
  color: "#f8fafc",
  padding: "8px 10px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 700,
};

const ifThenCardStyle = {
  padding: 14,
  borderRadius: 12,
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.12)",
  minHeight: 90,
  display: "grid",
  alignContent: "start",
  gap: 6,
};

const ifThenText = {
  color: "#e5e7eb",
  lineHeight: 1.4,
  fontSize: 15,
};

const pillLabel = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 999,
  background: "rgba(14,165,233,0.18)",
  color: "#bae6fd",
  border: "1px solid rgba(14,165,233,0.4)",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: "uppercase",
};

const labelStyle = {
  display: "block",
  marginBottom: 4,
  fontWeight: 700,
  letterSpacing: 0.3,
  color: "#cbd5e1",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "rgba(15,23,42,0.75)",
  color: "#e2e8f0",
  fontSize: 14,
  resize: "vertical",
};

const saveButtonStyle = {
  border: "none",
  background: "linear-gradient(120deg, #38bdf8, #facc15)",
  color: "#0b0f1a",
  padding: "10px 16px",
  borderRadius: 12,
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 8px 30px rgba(250,204,21,0.3)",
};
